import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

import { useUser } from '../../../../context/UserContext';
import useOpenAlerts from '../../../../components/alerts/useOpenAlerts';
import { SEVERITY } from '../../../../components/alerts/alertLabels';

/**
 * Campana con las alertas activas (OPEN). El administrador ve las de todos los
 * sensores; el resto de usuarios, solo las de sus sensores.
 */
const AlertsBell = () => {
    const { user } = useUser();
    const { alerts, total, enabled, isAdmin, refresh } = useOpenAlerts(user);
    const [isOpen, setIsOpen] = useState(false);

    if (!enabled) return null;

    const toggle = () => {
        if (!isOpen) refresh();
        setIsOpen((open) => !open);
    };

    return (
        <div className="inline-block py-2 sm:relative">
            <button type="button" onClick={toggle} className="relative flex items-center text-gray-50 hover:text-blue-600" aria-label="Alertas">
                <span className="material-icons text-3xl">notifications</span>
                {total > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
                        {total > 99 ? '99+' : total}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="fixed inset-0 z-10" onClick={toggle}></div>
            )}

            {/* En móvil ocupa el ancho de la pantalla bajo el encabezado; desde sm se ancla a la campana */}
            <div className={`z-20 overflow-hidden transition-all duration-300 bg-white shadow-lg border border-gray-100
                fixed left-2 right-2 top-[70px] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80
                ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}
            >
                <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-800">
                    Alertas activas {isAdmin && <span className="font-normal text-gray-500">(todos los sensores)</span>}
                </div>

                <div className="max-h-[360px] overflow-y-auto">
                    {alerts.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-500">No hay alertas activas</p>
                    ) : (
                        alerts.map((alert) => (
                            <div key={alert.id} className="px-4 py-2 border-b border-gray-100 text-sm">
                                <div className="flex items-start">
                                    <span className={`mt-1.5 mr-2 w-2 h-2 rounded-full shrink-0 ${SEVERITY[alert.severity]?.dot || 'bg-gray-400'}`}></span>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-gray-900 truncate">{alert.title}</p>
                                        <p className="text-gray-600 truncate">{alert.sensorName}</p>
                                        <p className="text-xs text-gray-400">
                                            {formatDistanceToNow(new Date(alert.firstDetectedAt), { addSuffix: true, locale: es })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <Link to="/alerts" onClick={toggle} className="block px-4 py-2 text-sm text-center text-blue-600 hover:bg-gray-100 no-underline">
                    Ver todas las alertas
                </Link>
            </div>
        </div>
    );
};

export default AlertsBell;
